import React from "react";
import { CalendarDays, Map as MapIcon, Minus, Plus, Printer, Redo2, Target, Undo2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import PrintDialog from "@/components/dashboard/PrintDialog";
import PrintPlusDialog from "@/components/dashboard/PrintPlusDialog";
import { LiveIndicator } from "@/components/dashboard/LiveIndicator";

export default function PlanningToolbar({ data, cell, setCell, onToday, hist, doUndo, onPrint,
  jump, setJump, doSearch, hits, goToHit, onSwitchToMap }) {
  const { t } = useLang();
  return (
    <div className="px-5 pt-3 pb-2 flex flex-wrap items-center gap-2" data-testid="planning-toolbar">
      <Button variant="outline" size="sm" className="h-8" onClick={onSwitchToMap}
        title={t("toggle_view_hint")} data-testid="switch-to-map">
        <MapIcon className="h-4 w-4 mr-1" />{t("view_map")}
      </Button>

      <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1 shadow-sm">
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => setCell((c) => Math.max(12, c - 2))} data-testid="planning-zoom-out" title={t("zoom_out")}><Minus className="h-4 w-4" /></Button>
        <span className="text-[11px] tabular-nums text-muted-foreground w-9 text-center">{cell}px</span>
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => setCell((c) => Math.min(40, c + 2))} data-testid="planning-zoom-in" title={t("zoom_in")}><Plus className="h-4 w-4" /></Button>
      </div>

      <PrintDialog />
      <PrintPlusDialog />

      <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1 shadow-sm">
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={onToday} data-testid="planning-today" title={t("view_today")}><CalendarDays className="h-4 w-4" /></Button>
        {hist?.admin && (
          <>
            <span className="h-5 w-px bg-border" />
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={!hist.can_undo}
              title={`${t("undo")}${hist.rows?.[0] ? ` · ${t(`act_${hist.rows[0].action}`) || hist.rows[0].action}` : ""}`}
              onClick={() => doUndo("undo")} data-testid="planning-undo"><Undo2 className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={!hist.can_redo}
              title={t("redo")} onClick={() => doUndo("redo")} data-testid="planning-redo"><Redo2 className="h-4 w-4" /></Button>
          </>
        )}
        <span className="h-5 w-px bg-border" />
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={onPrint} data-testid="planning-print-button" title={t("print_planning")}><Printer className="h-4 w-4" /></Button>
      </div>

      <form className="relative flex items-center" onSubmit={(e) => { e.preventDefault(); doSearch(); }}>
        <button type="submit" title={t("map_search_btn")} data-testid="planning-jump"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
          <Target className="h-4 w-4" />
        </button>
        <Input value={jump} onChange={(e) => setJump(e.target.value)} placeholder={t("jump_search")} className="h-9 w-56 rounded-full pl-9" data-testid="planning-jump-input" />
        {hits && (
          <div className="absolute top-10 left-0 z-50 w-80 max-h-64 overflow-auto cb-scroll rounded-xl border border-border bg-card shadow-xl divide-y divide-slate-100"
            data-testid="planning-search-results">
            {hits.map((h) => (
              <button key={h.reservation_id} type="button" onClick={() => goToHit(h)} data-testid={`planning-hit-${h.reservation_id}`}
                className="w-full text-left px-3 py-2 hover:bg-muted">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <i className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: h.status === "checked_in" ? "#e11d48" : "#f59e0b" }} />
                  {h.guest_name} · {h.pitch_label}
                </span>
                <span className="block text-[11px] text-muted-foreground">{fmtDate(h.arrival)} → {fmtDate(h.departure)}{[h.license_plate, h.phone, h.email].filter(Boolean).map((v) => ` · ${v}`).join("")}</span>
              </button>
            ))}
          </div>
        )}
      </form>

      <div className="ml-auto flex items-center gap-2">
        <LiveIndicator />
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" aria-label={t("map_help")} data-testid="planning-help-button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/40 transition-colors">
              <HelpCircle className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="max-w-xs w-auto bg-blue-600 text-white border-blue-700 p-3 text-xs leading-relaxed" data-testid="planning-help-popover">
            {t("planning_hint")}
          </PopoverContent>
        </Popover>
      </div>
      </div>
  );
}
